<template>
  <v-input
    ref="root"
    v-slot="{ isValid }"
    :model-value="modelValue"
    :rules="validationRules"
    hide-details
  >
    <v-field
      variant="outlined"
      density="compact"
      color="primary"
      :label="label"
      active
      :focused="isFocused"
      :dirty="hasAnyValue"
      :error="isValid.value === false"
      :clearable="clearable && hasAnyValue"
      @click:clear="clearValue"
    >
      <template #prepend-inner>
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          location="bottom start"
          min-width="auto"
        >
          <template #activator="{ props: menuProps }">
            <v-icon
              v-bind="menuProps"
              icon="mdi-calendar"
              class="cursor-pointer"
            />
          </template>

          <v-locale-provider locale="en">
            <v-card
              ref="pickerCard"
              elevation="4"
              class="d-inline-block rounded-lg date-picker-card"
              style="max-width: 328px;"
              tabindex="0"
              @keydown="onPickerKeydown"
            >
              <v-date-picker
                :model-value="pendingAsDate"
                :month="pickerMonth"
                :year="pickerYear"
                hide-header
                color="primary"
                elevation="0"
                @update:model-value="onPickerSelect"
                @update:month="pickerMonth = $event"
                @update:year="pickerYear = $event"
              ></v-date-picker>
              <v-divider></v-divider>
              <div class="d-flex justify-space-between px-2 py-1">
                <v-btn variant="text" color="primary" size="small" @click="onClearClick">Clear</v-btn>
                <v-btn variant="text" color="primary" size="small" @click="onTodayClick">Today</v-btn>
              </div>
            </v-card>
          </v-locale-provider>
        </v-menu>
      </template>

      <div class="date-segments" @mousedown="onContainerMousedown">
        <input
          ref="day"
          :value="day"
          class="date-segment date-segment--dm"
          type="text"
          inputmode="numeric"
          maxlength="2"
          placeholder="DD"
          autocomplete="off"
          @input="onInput('day', $event)"
          @keydown="onKeydown('day', $event)"
          @focus="onSegmentFocus"
          @blur="onSegmentBlur('day')"
        />
        <span class="date-separator">/</span>
        <input
          ref="month"
          :value="month"
          class="date-segment date-segment--dm"
          type="text"
          inputmode="numeric"
          maxlength="2"
          placeholder="MM"
          autocomplete="off"
          @input="onInput('month', $event)"
          @keydown="onKeydown('month', $event)"
          @focus="onSegmentFocus"
          @blur="onSegmentBlur('month')"
        />
        <span class="date-separator">/</span>
        <input
          ref="year"
          :value="year"
          class="date-segment date-segment--y"
          type="text"
          inputmode="numeric"
          maxlength="4"
          placeholder="YYYY"
          autocomplete="off"
          @input="onInput('year', $event)"
          @keydown="onKeydown('year', $event)"
          @focus="onSegmentFocus"
          @blur="onSegmentBlur('year')"
        />
      </div>
    </v-field>
  </v-input>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const STORAGE_FORMAT = "YYYY-MM-DD";

type SegmentName = "day" | "month" | "year";

const SEGMENT_ORDER: SegmentName[] = ["day", "month", "year"];
const SEGMENT_LENGTH: Record<SegmentName, number> = { day: 2, month: 2, year: 4 };
const SEGMENT_MAX: Record<SegmentName, number> = { day: 31, month: 12, year: 9999 };

export default defineComponent({
  name: "DatePicker",

  props: {
    label: {
      type: String,
      default: "",
    },
    modelValue: {
      type: [String, null],
      default: "",
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      menu: false,
      day: "",
      month: "",
      year: "",
      isFocused: false,
      pending: "",
      pickerMonth: dayjs().month(),
      pickerYear: dayjs().year(),
    };
  },

  watch: {
    modelValue: {
      immediate: true,
      handler() {
        if (!this.isFocused) {
          this.syncSegments();
        }
      },
    },
    menu(open: boolean) {
      if (open) {
        this.initPicker();
        window.addEventListener('scroll', this.onScroll, true);
      } else {
        window.removeEventListener('scroll', this.onScroll, true);
      }
    },
  },

  computed: {
    hasAnyValue(): boolean {
      return Boolean(this.day || this.month || this.year);
    },
    validationRules(): Array<(value: string) => boolean | string> {
      return (this.rules as Array<(value: string) => boolean | string>).map(
        (rule) => () => rule(this.modelValue as string)
      );
    },
    pendingAsDate(): Date | undefined {
      if (this.pending) {
        const parsed = dayjs(this.pending, STORAGE_FORMAT, true);
        if (parsed.isValid()) {
          return parsed.toDate();
        }
      }
      return undefined;
    },
  },

  methods: {
    segmentEl(segment: SegmentName): HTMLInputElement | undefined {
      return this.$refs[segment] as HTMLInputElement | undefined;
    },
    focusSegment(segment: SegmentName) {
      const el = this.segmentEl(segment);
      if (el) {
        el.focus();
        el.select();
      }
    },
    syncSegments() {
      if (this.modelValue && dayjs(this.modelValue, STORAGE_FORMAT, true).isValid()) {
        this.applySegments(this.modelValue);
        return;
      }
      this.day = "";
      this.month = "";
      this.year = "";
    },
    applySegments(storageValue: string) {
      const parsed = dayjs(storageValue, STORAGE_FORMAT, true);
      this.day = parsed.format("DD");
      this.month = parsed.format("MM");
      this.year = parsed.format("YYYY");
    },
    padSegment(segment: SegmentName) {
      const value = this[segment];
      if (value) {
        this[segment] = value.padStart(SEGMENT_LENGTH[segment], "0");
      }
    },
    padAllSegments() {
      SEGMENT_ORDER.forEach((segment) => this.padSegment(segment));
    },
    onInput(segment: SegmentName, event: Event) {
      const el = event.target as HTMLInputElement;
      let sanitized = el.value.replace(/\D/g, "").slice(0, SEGMENT_LENGTH[segment]);

      const max = SEGMENT_MAX[segment];
      if (sanitized && segment !== "year" && parseInt(sanitized, 10) > max) {
        sanitized = String(max);
      }

      this[segment] = sanitized;
      if (el.value !== sanitized) {
        el.value = sanitized;
      }

      const complete =
        sanitized.length === SEGMENT_LENGTH[segment] ||
        (sanitized !== "" && parseInt(sanitized, 10) * 10 > max);
      if (segment !== "year" && complete) {
        this.padSegment(segment);
        const next = SEGMENT_ORDER[SEGMENT_ORDER.indexOf(segment) + 1];
        this.focusSegment(next);
      }
    },
    onKeydown(segment: SegmentName, event: KeyboardEvent) {
      const el = event.target as HTMLInputElement;
      const index = SEGMENT_ORDER.indexOf(segment);

      if (this.menu) {
        this.onPickerKeydown(event);
        if (event.defaultPrevented) {
          return;
        }
      }

      if (event.key === "Tab") {
        this.padSegment(segment);
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        this.commitSegments();
        return;
      }

      const fullySelected =
        el.value.length > 0 &&
        el.selectionStart === 0 &&
        el.selectionEnd === el.value.length;

      if (event.key === "ArrowRight") {
        const atEnd =
          fullySelected ||
          (el.selectionStart === el.value.length && el.selectionEnd === el.value.length);
        if (atEnd && index < SEGMENT_ORDER.length - 1) {
          event.preventDefault();
          this.padSegment(segment);
          this.focusSegment(SEGMENT_ORDER[index + 1]);
        }
        return;
      }

      if (event.key === "ArrowLeft") {
        const atStart =
          fullySelected || (el.selectionStart === 0 && el.selectionEnd === 0);
        if (atStart && index > 0) {
          event.preventDefault();
          this.focusSegment(SEGMENT_ORDER[index - 1]);
        }
        return;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        this.stepSegment(segment, event.key === "ArrowUp" ? 1 : -1);
        this.$nextTick(() => {
          const target = this.segmentEl(segment);
          if (target) {
            target.select();
          }
        });
        return;
      }

      if (event.key === "Backspace") {
        if (!this[segment] && index > 0) {
          event.preventDefault();
          this.focusSegment(SEGMENT_ORDER[index - 1]);
        }
        return;
      }

      if (event.key.length === 1 && !/\d/.test(event.key)) {
        event.preventDefault();
      }
    },
    stepSegment(segment: SegmentName, delta: number) {
      const now = dayjs();
      const current = this[segment]
        ? parseInt(this[segment], 10)
        : segment === "day"
          ? now.date()
          : segment === "month"
            ? now.month() + 1
            : now.year();

      const min = 1;
      let max = 9999;
      if (segment === "day") {
        max = this.daysInCurrentMonth();
      } else if (segment === "month") {
        max = 12;
      }

      let next = current + delta;
      if (segment === "year") {
        next = Math.min(max, Math.max(min, next));
      } else if (next > max) {
        next = min;
      } else if (next < min) {
        next = max;
      }
      this[segment] = String(next).padStart(SEGMENT_LENGTH[segment], "0");
    },
    daysInCurrentMonth(): number {
      const month = parseInt(this.month, 10);
      if (!month || month < 1 || month > 12) {
        return 31;
      }
      const year = parseInt(this.year, 10) || dayjs().year();
      const base = dayjs(new Date(year, month - 1, 1));
      return base.isValid() ? base.daysInMonth() : 31;
    },
    onSegmentFocus(event: FocusEvent) {
      this.isFocused = true;
      const el = event.target as HTMLInputElement;
      requestAnimationFrame(() => el.select());
    },
    onSegmentBlur(segment: SegmentName) {
      this.padSegment(segment);
      requestAnimationFrame(() => {
        const root = (this.$refs.root as { $el?: HTMLElement } | undefined)?.$el;
        const active = document.activeElement;
        if (root && active && root.contains(active)) {
          return;
        }
        this.isFocused = false;
        this.commitSegments();
      });
    },
    onContainerMousedown(event: MouseEvent) {
      if ((event.target as HTMLElement).tagName !== "INPUT") {
        event.preventDefault();
        this.focusSegment(this.day ? (this.month ? "year" : "month") : "day");
      }
    },
    commitSegments() {
      this.padAllSegments();

      if (!this.day && !this.month && !this.year) {
        if (this.modelValue !== "") {
          this.$emit("update:modelValue", "");
        }
        return;
      }

      const candidate = `${this.year}-${this.month}-${this.day}`;
      const parsed = dayjs(candidate, STORAGE_FORMAT, true);
      if (parsed.isValid()) {
        if (candidate !== this.modelValue) {
          this.$emit("update:modelValue", candidate);
        }
      }
      // invalid / partial: keep the digits the user typed, do not revert or clear
    },
    clearValue() {
      this.day = "";
      this.month = "";
      this.year = "";
      this.$emit("update:modelValue", "");
    },
    initPicker() {
      const valid =
        this.modelValue && dayjs(this.modelValue, STORAGE_FORMAT, true).isValid();
      this.pending = valid
        ? (this.modelValue as string)
        : dayjs().format(STORAGE_FORMAT);
      this.syncPickerView();
      this.$nextTick(() => {
        const card = this.$refs.pickerCard as { $el?: HTMLElement } | undefined;
        card?.$el?.focus({ preventScroll: true });
      });
    },
    syncPickerView() {
      const parsed = dayjs(this.pending, STORAGE_FORMAT, true);
      if (parsed.isValid()) {
        this.pickerMonth = parsed.month();
        this.pickerYear = parsed.year();
      }
    },
    movePending(days: number) {
      const base = dayjs(this.pending, STORAGE_FORMAT, true);
      if (!base.isValid()) {
        this.pending = dayjs().format(STORAGE_FORMAT);
      } else {
        this.pending = base.add(days, "day").format(STORAGE_FORMAT);
      }
      this.syncPickerView();
    },
    onPickerKeydown(event: KeyboardEvent) {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          this.movePending(-1);
          break;
        case "ArrowRight":
          event.preventDefault();
          this.movePending(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          this.movePending(-7);
          break;
        case "ArrowDown":
          event.preventDefault();
          this.movePending(7);
          break;
        case "Enter":
          event.preventDefault();
          this.selectDate(this.pending);
          break;
      }
    },
    onPickerSelect(val: Date | undefined) {
      if (val) {
        this.selectDate(dayjs(val).format(STORAGE_FORMAT));
      }
    },
    selectDate(storageValue: string) {
      if (dayjs(storageValue, STORAGE_FORMAT, true).isValid()) {
        this.$emit("update:modelValue", storageValue);
        this.applySegments(storageValue);
      }
      this.menu = false;
    },
    onClearClick() {
      this.clearValue();
      this.menu = false;
    },
    onTodayClick() {
      this.selectDate(dayjs().format(STORAGE_FORMAT));
    },
    onScroll() {
      this.menu = false;
    },
  },
});
</script>

<style scoped>
.date-segments {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 42px;
  cursor: text;
}

.date-segment {
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  text-align: center;
  font: inherit;
  color: inherit;
  min-width: 0;
}

.date-segment--dm {
  width: 28px;
}

.date-segment--y {
  width: 44px;
}

.date-segment::placeholder {
  color: rgba(var(--v-theme-on-surface), 0.38);
}

.date-separator {
  user-select: none;
  padding: 0 2px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.date-picker-card {
  outline: none;
}
</style>
